import { envConfig } from "config/envConfig"
import { Organization, Role, Team } from "interface/user"
import { AdminCareOrganizations } from "modules/admin/AdminCareOrganizations"
import { AdminEnrollments } from "modules/admin/AdminEnrollments"
import { AdminGlobalConfig } from "modules/admin/AdminGlobalConfig"
import { AdminLayout } from "modules/admin/AdminLayout"
import { AdminUsers } from "modules/admin/AdminUsers"
import { AdminVisits } from "modules/admin/AdminVisits"
import { Chat } from "modules/communications/Chat"
import { Voice } from "modules/communications/Voice"
import { PatientDashboard } from "modules/dashboard/PatientDashboard"
import { Screenings } from "modules/dashboard/Screenings"
import { Enrollments } from "modules/enrollments/Enrollments"
import { EscalationDetail } from "modules/escalations/EscalationDetail"
import { Flag, FLAGS, useFlag } from "modules/flags/flags"
import { HelpPage } from "modules/help/Help"
import { LegalAgreement } from "modules/legal/LegalAgreement"
import {
	initSentryUser,
	wrapCreateBrowserRouterV7,
} from "modules/monitoring/monitoring"
import { NotificationSettings } from "modules/notifications/NotificationSettings"
import { RealtimeProvider } from "modules/realtime/RealtimeProvider"
import { AuthLayout } from "pages/auth/AuthLayout"
import { CheckAuthorization } from "pages/auth/CheckAuthorization"
import { Login } from "pages/auth/Login"
import { PageNotFound } from "pages/auth/PageNotFound"
import { ProtectedRoute } from "pages/auth/ProtectedRoute"
import { SessionExpired } from "pages/auth/SessionExpired"
import { SetPassword } from "pages/auth/SetPassword"
import { Unauthorized } from "pages/auth/Unauthorized"
import { Billing } from "pages/dashboard/Billing/Billing"
import { DashboardLayout } from "pages/dashboard/DashboardLayout"
import { PatientsList } from "pages/dashboard/PatientsList/PatientsList"
import { PatientLayout } from "pages/patient/PatientLayout"
import { Version } from "pages/Version"
import { AllVisits } from "pages/visits/AllVisits"
import { MyVisits } from "pages/visits/MyVisits"
import { NeedsScheduling } from "pages/visits/NeedsScheduling"
import { StartVisit } from "pages/visits/StartVisit"
import { UnscheduledVisit } from "pages/visits/UnscheduledVisit"
import { VisitsLayout } from "pages/visits/VisitsLayout"
import { useEffect, useMemo } from "react"
import {
	createBrowserRouter,
	matchPath,
	RouterProvider,
	useLocation,
} from "react-router"
import { useOrganization, useTeam, useUserRole } from "services/users"

export function PageRouter() {
	const router = window.location.hostname.startsWith(envConfig.helpSubdomain())
		? helpRouter
		: appRouter
	return <RouterProvider router={router} />
}

const ChatWindow = () => <Chat.Window />

const CHAT_ROUTE = {
	path: "patients/:userId/m/chat/:activeParticipantId",
	element: <ChatWindow />,
	checkPermissions: (team: Team) => team.canVisit || team.scheduling !== "none",
}

const CHAT_ROUTE_DASHBOARD = {
	...CHAT_ROUTE,
	checkPermissions: (organization: Organization) =>
		organization.hasView || organization.hasSend,
}

const CHAT_ROUTE_VISITS = {
	...CHAT_ROUTE,
	checkPermissions: (team: Team) => team.canVisit || team.scheduling !== "none",
}

export const ROUTES = [
	{
		path: "/",
		roles: ["Provider", "Provider-anon"],
		element: (
			<ProtectedRoute>
				<RealtimeProvider>
					<Voice.Controls>
						<DashboardLayout />
					</Voice.Controls>
				</RealtimeProvider>
				<LegalAgreement />
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				path: ":scopeId",
				element: <PatientsList />,
			},
			{
				path: ":scopeId/patients/:userId",
				element: <PatientDashboard />,
			},
			{
				path: ":scopeId/patients/:userId/escalations/:escalationId",
				element: <EscalationDetail />,
				roles: ["Provider"],
				flags: ["clinicalEscalations"],
			},
			{
				path: ":scopeId/patients/:userId/m/screenings/:dataId",
				element: <Screenings />,
			},
			{
				path: ":scopeId/enrollments",
				element: <Enrollments />,
				roles: ["Provider"],
				checkPermissions: (organization: Organization) =>
					organization.canEnrollPatients,
			},
			{
				path: ":scopeId/billing",
				element: <Billing />,
				roles: ["Provider"],
			},
			{
				path: ":scopeId/settings/notifications",
				element: <NotificationSettings />,
				roles: ["Provider"],
				flags: ["clinicalEscalations"],
			},
			...[
				":scopeId",
				":scopeId/patients/:userId",
				":scopeId/patients/:userId/m/screenings/:dataId",
				":scopeId/enrollments",
				":scopeId/billing",
			].map((basePath) => ({
				...CHAT_ROUTE_DASHBOARD,
				path: `${basePath}/${CHAT_ROUTE_DASHBOARD.path}`,
			})),
		],
	},
	{
		path: "/me",
		roles: ["Patient"],
		element: (
			<ProtectedRoute>
				<RealtimeProvider>
					<PatientLayout />
				</RealtimeProvider>
				<LegalAgreement />
			</ProtectedRoute>
		),
	},
	{
		path: "/:scopeId/visits",
		roles: ["Field-support-agent"],
		checkPermissions: (team: Team) => team.canVisit,
		element: (
			<ProtectedRoute>
				<RealtimeProvider>
					<Voice.Controls>
						<VisitsLayout />
					</Voice.Controls>
				</RealtimeProvider>
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				element: <MyVisits />,
				checkPermissions: (team: Team) => team.canVisit,
			},
			{
				path: "all",
				element: <AllVisits />,
				checkPermissions: (team: Team) =>
					["view", "edit"].includes(team.scheduling),
			},
			{
				path: "unscheduled",
				element: <NeedsScheduling />,
				checkPermissions: (team: Team) => team.scheduling === "edit",
			},
			{
				path: "unscheduled/:visitId",
				element: <UnscheduledVisit />,
				checkPermissions: (team: Team) => team.scheduling === "edit",
			},
			{
				path: ":visitId",
				element: <StartVisit />,
				checkPermissions: (team: Team) => team.canVisit,
			},
			CHAT_ROUTE_VISITS,
			...["all", "unscheduled", "unscheduled/:visitId", ":visitId"].map(
				(basePath) => ({
					...CHAT_ROUTE_VISITS,
					path: `${basePath}/${CHAT_ROUTE_VISITS.path}`,
				}),
			),
		],
	},
	{
		path: "/admin",
		roles: ["Administrator"],
		flags: [
			"adminEnrollments",
			"adminUsers",
			"adminConfigs",
			"adminVisits",
			"adminCareOrganizations",
		],
		element: (
			<ProtectedRoute>
				<AdminLayout />
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				path: "users",
				element: <AdminUsers />,
				flags: ["adminUsers"],
			},
			{
				path: "enrollments",
				element: <AdminEnrollments />,
				flags: ["adminEnrollments"],
			},
			{
				path: "global-config",
				element: <AdminGlobalConfig />,
				flags: ["adminConfigs"],
			},
			{
				path: "visits",
				element: <AdminVisits />,
				flags: ["adminVisits"],
			},
			{
				path: "care-organizations",
				element: <AdminCareOrganizations />,
				flags: ["adminCareOrganizations"],
			},
		],
	},
	{
		element: <AuthLayout />,
		children: [
			{
				path: "/login",
				element: <Login />,
			},
			{
				path: "/resetPassword",
				element: <SetPassword />,
			},
		],
	},
	{
		path: "/check-authorization",
		element: <CheckAuthorization />,
	},
	{
		path: "/version",
		element: <Version />,
	},
	{
		path: "/unauthorized",
		element: <Unauthorized />,
	},
	{
		path: "/session-expired",
		element: <SessionExpired />,
	},
	{
		path: "*",
		element: <PageNotFound />,
	},
]

export function useAuthorizedRoutes() {
	const user = useUserRole()
	const team = useTeam()
	const organization = useOrganization()
	const location = useLocation()
	const featureFlags = useFlag(...(Object.keys(FLAGS) as Flag[]))
	const { allowedRoutes, activeRouteIsAuthorized } = useMemo(() => {
		type CheckPermissionsFn = (scope: Team | Organization) => boolean
		const flatRoutes: (Pick<
			(typeof ROUTES)[number],
			"path" | "roles" | "flags"
		> & {
			checkPermissions: CheckPermissionsFn
		})[] = []
		const extractPaths = ({
			routes,
			parentPath,
			parentRoles,
			parentFlags,
		}: {
			routes: typeof ROUTES
			parentPath?: string
			parentRoles?: Role[]
			parentFlags?: Flag[]
		}) => {
			for (const route of routes) {
				const { path, roles, flags, children } = route
				let finalPath = path
				if (path) {
					let newParentPath = ""
					if (parentPath) {
						newParentPath = parentPath.endsWith("/")
							? parentPath
							: `${parentPath}/`
					}
					finalPath = `${newParentPath}${path}`
					flatRoutes.push({
						path: finalPath,
						roles: roles ?? parentRoles,
						flags: flags ?? parentFlags,
						checkPermissions: route.checkPermissions as CheckPermissionsFn,
					})
				}

				if (children) {
					extractPaths({
						routes: children as typeof ROUTES,
						parentPath: finalPath,
						parentRoles: (roles ?? parentRoles) as Role[],
						parentFlags: (flags ?? parentFlags) as Flag[],
					})
				}
			}
		}

		extractPaths({ routes: ROUTES })

		const allRoutes = flatRoutes
			.filter(({ roles = [], flags = [], checkPermissions }) => {
				const hasRole = user.data?.role && roles.includes(user.data?.role)
				const hasFlags =
					flags.length === 0 ||
					flags.every((flag) => featureFlags[flag as Flag]?.enabled)
				let hasPermissions = checkPermissions === undefined
				if (checkPermissions && team) {
					hasPermissions = checkPermissions(team)
				} else if (checkPermissions && organization) {
					hasPermissions = checkPermissions(organization)
				}

				return hasRole && hasFlags && hasPermissions
			})
			.map(({ path }) => path)
			.filter(Boolean) as string[]

		const allowedRoutes = allRoutes.length > 0 ? allRoutes : ["/unauthorized"]
		const matchedRoutes = allowedRoutes.filter((path) => {
			return matchPath(path, location.pathname)
		})
		return {
			allowedRoutes,
			activeRouteIsAuthorized: matchedRoutes.length > 0,
		}
	}, [featureFlags, location.pathname, organization, team, user.data?.role])

	useEffect(() => {
		!!user.data?.id && initSentryUser({ id: user.data.id })
	}, [user.data?.id])

	return {
		isFetched: user.isFetched,
		allowedRoutes,
		activeRouteIsAuthorized,
	}
}

const appRouter = wrapCreateBrowserRouterV7(createBrowserRouter)(ROUTES)

const helpRouter = wrapCreateBrowserRouterV7(createBrowserRouter)([
	{ path: "*", element: <HelpPage /> },
])
